/*----------------------------------
- CONSTANTS
----------------------------------*/

export const FREE_TRIAL_MAX_CONTACTS = 50;
export const FREE_TRIAL_DURATION_MS = 72 * 60 * 60 * 1000;
export const FREE_TRIAL_CAMPAIGN_SOURCE = 'network-scanner-demo';
export const NETWORK_SCANNER_DEMO_FUNNEL = 'network-scanner';
export const NETWORK_SCANNER_DEMO_MAIN_FEATURE = '/dashboard/free-trial';
export const NETWORK_SCANNER_UNLOCKED_MAIN_FEATURE = '/dashboard/crm-network-report';
export const FREE_TRIAL_CONVERSION_OFFER = {
    planLabel: 'Pro',
    firstMonthCents: 1900,
    recurringCents: 9000,
    billingPeriod: 'monthly',
    discountDurationMonths: 1,
    discountAmountCents: 7100,
} as const;
export const FREE_TRIAL_CONVERSION_FEATURE_FLAGS = {
    hasCrm: false,
    hasAts: false,
    hasBizdevSearch: true,
    hasBizdevCampaigns: true,
    hasBizdevCrm: true,
    hasBizdevBdPartners: false,
    // Mandates, ATS Pipeline and Talent Pool: a free user has them, so the trial must not take them away.
    hasSourcingActiveRoles: true,
    hasSourcingSearch: false,
    hasSourcingCampaigns: false,
    hasSourcingAts: false,
    hasAmbassadorAffiliation: false,
    hasAmbassadorPackCalculator: false,
    hasCrmNetworkScan: true,
    hasCrmNetworkScanUpdates: true,
} as const;
const LINKEDIN_PROFILE_URL_REGEXP = /(?:https?:\/\/)?(?:[\w-]+\.)?linkedin\.com\/in\/([^/?#]+)/i;
const CALENDLY_PLACEHOLDER = '{{Calendly Link}}';

/*----------------------------------
- TYPES
----------------------------------*/

export type TrialStep = 1 | 2 | 3;

export type TrialContact = {
    id: string;
    name: string;
    firstName: string;
    lastName: string;
    linkedinId: string;
    linkedinUrl: string;
    headline: string | null;
    currentCompany: string;
    previousCompanies: string[];
    hasJobOpenings: boolean;
    profilePictureUrl: string | null;
};

export type FreeTrialSeedContactInput = {
    id: string;
    name: string;
    firstName: string;
    lastName: string;
    headline: string | null;
    linkedinUrl: string | null;
    profileIdentifier: string | null;
};

export type TrialSignature = {
    firstName: string;
    lastName: string;
    company: string;
    role: string;
    phone: string;
    calendlyLink: string;
};

export type TrialMessageTemplate = {
    id: string;
    subject: string;
    delayDays: number;
    content: string;
};

export type TrialMessage = TrialMessageTemplate & {
    isEdited: boolean;
    isSaved: boolean;
};

export type FreeTrialStatus = 'active' | 'expired' | 'unlocked';
export type FreeTrialView = 'wizard' | 'expired' | 'unlocked';

export type FreeTrialImportContact = Pick<
    TrialContact,
    | 'id'
    | 'name'
    | 'firstName'
    | 'lastName'
    | 'linkedinId'
    | 'linkedinUrl'
    | 'headline'
    | 'currentCompany'
    | 'previousCompanies'
    | 'hasJobOpenings'
    | 'profilePictureUrl'
> & {
    hiringCompaniesCount?: number;
    jobsCount?: number;
    topCompanies?: string[];
};

export type FreeTrialMessageInput = Pick<TrialMessageTemplate, 'id' | 'subject' | 'delayDays' | 'content'>;

export type FreeTrialImportedCampaignSummary = {
    id: string;
    name: string;
};

export type FreeTrialScanStatus = 'pending' | 'running' | 'completed' | 'failed';

export type FreeTrialScanContactProcessingState = 'hiring' | 'processing' | 'pending' | 'non_hiring';

export type FreeTrialScanContactResult = Omit<FreeTrialImportContact, 'linkedinId' | 'linkedinUrl' | 'currentCompany'> & {
    linkedinId: string | null;
    linkedinUrl: string | null;
    currentCompany: string | null;
    location: string | null;
    processingState: FreeTrialScanContactProcessingState;
    statusReason: string | null;
};

export type FreeTrialScanState = {
    scanId: string;
    startedAt: string | null;
    status: FreeTrialScanStatus;
    progressPercent: number;
    progressStageLabel: string;
    progressEtaSeconds: number | null;
    totalSeedContactsCount: number;
    scannableContactsCount: number;
    skippedContactsCount: number;
    processedContactsCount: number;
    rows: FreeTrialScanContactResult[];
    skippedRows: Array<{
        id: string;
        name: string;
        headline: string | null;
        linkedinUrl: string | null;
        reason: string | null;
    }>;
};

export type FreeTrialStartScanSource = 'linkedin-connections';

export type FreeTrialStartScanPayload = {
    source: FreeTrialStartScanSource;
    contacts: FreeTrialSeedContactInput[];
};

export type FreeTrialVerifyOwnerLinkedinUrlPayload = {
    linkedInUrl: string;
};

export type FreeTrialVerifyOwnerLinkedinUrlResult = {
    normalizedLinkedinUrl: string;
};

export type FreeTrialImportPayload = {
    scanId: string;
    selectedContactIds: string[];
    signature: TrialSignature;
    messages: FreeTrialMessageInput[];
};

export type FreeTrialSaveSignaturePayload = {
    signature: TrialSignature;
};

export type FreeTrialSaveSignatureResult = {
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
};

export type FreeTrialImportResult = {
    campaignId: string;
    redirectPath: string;
    requiresCheckout: boolean;
};

export type FreeTrialState = {
    status: FreeTrialStatus;
    view: FreeTrialView;
    isActiveDemo: boolean;
    isExpired: boolean;
    hasUnlockedScanner: boolean;
    startedAt: string | null;
    expiresAt: string | null;
    remainingMs: number;
    packId: string | null;
    importedCampaign: FreeTrialImportedCampaignSummary | null;
    checkoutPath: string | null;
    activeScanId: string | null;
    ownerLinkedinUrl: string | null;
};

export type FreeTrialCampaignMetadata = {
    source: typeof FREE_TRIAL_CAMPAIGN_SOURCE;
    packId: string;
    locked: boolean;
    signature: TrialSignature;
    updatedAt: string;
    contacts: FreeTrialImportContact[];
};

/*----------------------------------
- HELPERS
----------------------------------*/

export const formatFreeTrialEuroAmount = (amountCents: number): string => {
    const amount = amountCents / 100;

    if (Number.isInteger(amount)) {
        return `€${amount}`;
    }

    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
};

export const FREE_TRIAL_CONVERSION_PLAN_COPY = `${FREE_TRIAL_CONVERSION_OFFER.planLabel} plan`;
export const FREE_TRIAL_CONVERSION_BILLING_COPY =
    `${formatFreeTrialEuroAmount(FREE_TRIAL_CONVERSION_OFFER.firstMonthCents)} for the first month, then ` +
    `${formatFreeTrialEuroAmount(FREE_TRIAL_CONVERSION_OFFER.recurringCents)}/month`;

export const normalizeFreeTrialLinkedinProfileUrl = (value: string | null | undefined): string | null => {
    const trimmedValue = value?.trim();
    if (!trimmedValue) {
        return null;
    }

    const matchedIdentifier = trimmedValue.match(LINKEDIN_PROFILE_URL_REGEXP)?.[1]?.trim();
    if (!matchedIdentifier) {
        return null;
    }

    return `https://www.linkedin.com/in/${matchedIdentifier}`;
};

export const hasFreeTrialSignatureContent = (signature: TrialSignature): boolean =>
    Boolean(
        signature.firstName.trim() ||
            signature.lastName.trim() ||
            signature.company.trim() ||
            signature.role.trim() ||
            signature.phone.trim() ||
            signature.calendlyLink.trim(),
    );

export const replaceFreeTrialCalendlyPlaceholder = (text: string, signature: TrialSignature): string => {
    const normalizedCalendlyLink = signature.calendlyLink?.trim() || '';

    if (normalizedCalendlyLink) {
        return text.replaceAll(CALENDLY_PLACEHOLDER, normalizedCalendlyLink);
    }

    return text
        .split('\n')
        .filter((line) => !line.includes(CALENDLY_PLACEHOLDER))
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
};
